// composables/useDealerFallback.js
import { ref, computed } from 'vue'

export function useDealerFallback() {
  const isShowingOnlineRetailers = ref(false)
  const isMixedSearch = ref(false)
  const appliedFilters = ref([])
  const appliedRadius = ref(0)
  const localProducts = ref([])
  const onlineProducts = ref([])

  // Check if we should trigger fallback search
  const shouldTriggerFallback = (searchResults, hasFilters, radius) => {
    return searchResults.length === 0 && hasFilters && radius > 0
  }

  // Set fallback state when showing only online retailers
  const setFallbackState = (filters, radius) => {
    isShowingOnlineRetailers.value = true
    isMixedSearch.value = false
    appliedFilters.value = [...filters]
    appliedRadius.value = radius
    localProducts.value = []
    onlineProducts.value = [...filters]
  }

  // Set mixed search state when showing both local dealers and online retailers
  const setMixedSearchState = (localProds, onlineProds, radius) => {
    isShowingOnlineRetailers.value = false
    isMixedSearch.value = true
    appliedRadius.value = radius
    localProducts.value = [...localProds]
    onlineProducts.value = [...onlineProds]
    appliedFilters.value = [...localProds, ...onlineProds]
  }

  // Clear all fallback and mixed search states
  const clearFallbackState = () => {
    isShowingOnlineRetailers.value = false
    isMixedSearch.value = false
    appliedFilters.value = []
    appliedRadius.value = 0
    localProducts.value = []
    onlineProducts.value = []
  }

  // Generate fallback message for online-only retailers
  const fallbackMessage = computed(() => {
    if (!isShowingOnlineRetailers.value) return ''
    
    const filterNames = appliedFilters.value.join(', ')
    return `No dealers found within ${appliedRadius.value} miles, but here are online retailers that sell ${filterNames}.`
  })

  // Generate mixed search message
  const mixedSearchMessage = computed(() => {
    if (!isMixedSearch.value) return ''
    
    const localList = localProducts.value.join(', ')
    const onlineList = onlineProducts.value.join(', ')
    
    return `Local dealers found for ${localList}. Online retailers available for ${onlineList}.`
  })

  // Generate fallback title
  const fallbackTitle = computed(() => {
    if (isShowingOnlineRetailers.value) return 'Online Retailers Available'
    if (isMixedSearch.value) return 'Mixed Results Found'
    return ''
  })

  return {
    isShowingOnlineRetailers,
    isMixedSearch,
    appliedFilters,
    appliedRadius,
    localProducts,
    onlineProducts,
    shouldTriggerFallback,
    setFallbackState,
    setMixedSearchState,
    clearFallbackState,
    fallbackMessage,
    mixedSearchMessage,
    fallbackTitle
  }
}
